import React, { useState, useEffect, useRef, useCallback } from "react";
import "@/App.css";
import axios from "axios";
import { 
  Mic, MicOff, Volume2, VolumeX, Upload, 
  Trash2, Brain, X, Send, FileText, Sparkles, 
  Calendar, Radio, Search, ChevronRight, Clock
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function App() {
  // Chat state
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  
  // Voice state
  const [isListening, setIsListening] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [voiceEnabled, setVoiceEnabled] = useState(true);
  const [voiceMode, setVoiceMode] = useState(false); // Full voice conversation mode
  
  // Document state
  const [uploadedDocument, setUploadedDocument] = useState(null);
  const [documentContext, setDocumentContext] = useState("");
  const [showDocumentPanel, setShowDocumentPanel] = useState(false);
  
  // Memory state
  const [memories, setMemories] = useState([]);
  const [showMemoryPanel, setShowMemoryPanel] = useState(false);
  
  // Meeting state
  const [showMeetingsPanel, setShowMeetingsPanel] = useState(false);
  const [meetings, setMeetings] = useState([]);
  const [isRecordingMeeting, setIsRecordingMeeting] = useState(false);
  const [meetingSearch, setMeetingSearch] = useState("");
  const [selectedMeeting, setSelectedMeeting] = useState(null);
  
  // Refs
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const audioRef = useRef(null);
  const meetingRecorderRef = useRef(null);
  const meetingChunksRef = useRef([]);

  // Scroll to bottom of messages
  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  // Load chat history and memories on mount
  useEffect(() => {
    loadChatHistory();
    loadMemories();
    loadMeetings();
  }, []);

  const loadChatHistory = async () => {
    try {
      const response = await axios.get(`${API}/chat/history`);
      setMessages(response.data.messages || []);
    } catch (error) {
      console.error("Failed to load chat history:", error);
    }
  };

  const loadMemories = async () => {
    try {
      const response = await axios.get(`${API}/memories`);
      setMemories(response.data.memories || []);
    } catch (error) {
      console.error("Failed to load memories:", error);
    }
  };

  const loadMeetings = async (search = "") => {
    try {
      const params = search ? `?search=${encodeURIComponent(search)}` : "";
      const response = await axios.get(`${API}/meetings${params}`);
      setMeetings(response.data.meetings || []);
    } catch (error) {
      console.error("Failed to load meetings:", error);
    }
  };

  // Meeting recording functions
  const startMeetingRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      
      // Check supported MIME types
      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus') 
        ? 'audio/webm;codecs=opus' 
        : MediaRecorder.isTypeSupported('audio/webm') 
          ? 'audio/webm' 
          : 'audio/mp4';
      
      meetingRecorderRef.current = new MediaRecorder(stream, { mimeType });
      meetingChunksRef.current = [];

      meetingRecorderRef.current.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          meetingChunksRef.current.push(event.data);
        }
      };

      meetingRecorderRef.current.onstop = async () => {
        const audioBlob = new Blob(meetingChunksRef.current, { type: mimeType });
        stream.getTracks().forEach(track => track.stop());
        
        if (audioBlob.size === 0) {
          console.error("No audio data recorded");
          alert("No audio was recorded. Please check microphone permissions.");
          return;
        }
        
        // Upload and process meeting
        const formData = new FormData();
        const extension = mimeType.includes('webm') ? 'webm' : 'mp4';
        formData.append('audio', audioBlob, `meeting.${extension}`);
        formData.append('title', `Meeting ${new Date().toLocaleDateString()} ${new Date().toLocaleTimeString()}`);
        formData.append('topic', 'general');
        formData.append('priority', 'normal');
        
        try {
          setIsLoading(true);
          const response = await axios.post(`${API}/meetings/quick-record`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          console.log("Meeting recorded:", response.data);
          loadMeetings();
          setShowMeetingsPanel(true);
        } catch (error) {
          console.error("Meeting processing error:", error);
          alert("Error processing meeting. Please try again.");
        } finally {
          setIsLoading(false);
        }
      };

      meetingRecorderRef.current.onerror = (event) => {
        console.error("MediaRecorder error:", event.error);
      };

      // Start recording with timeslice to collect data periodically
      meetingRecorderRef.current.start(1000);
      setIsRecordingMeeting(true);
    } catch (error) {
      console.error("Microphone access error:", error);
      alert("Could not access microphone. Please allow microphone permissions.");
    }
  };

  const stopMeetingRecording = () => {
    if (meetingRecorderRef.current && isRecordingMeeting) {
      meetingRecorderRef.current.stop();
      setIsRecordingMeeting(false);
    }
  };

  // Voice conversation mode
  const startVoiceConversation = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      
      // Check supported MIME types
      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus') 
        ? 'audio/webm;codecs=opus' 
        : MediaRecorder.isTypeSupported('audio/webm') 
          ? 'audio/webm' 
          : 'audio/mp4';
      
      mediaRecorderRef.current = new MediaRecorder(stream, { mimeType });
      audioChunksRef.current = [];

      mediaRecorderRef.current.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorderRef.current.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: mimeType });
        stream.getTracks().forEach(track => track.stop());
        
        if (audioBlob.size === 0) {
          console.error("No audio data recorded");
          setIsListening(false);
          return;
        }
        
        // Send to voice conversation endpoint
        const formData = new FormData();
        const extension = mimeType.includes('webm') ? 'webm' : 'mp4';
        formData.append('audio', audioBlob, `voice.${extension}`);
        
        try {
          setIsLoading(true);
          const response = await axios.post(`${API}/voice/conversation`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          
          // Add messages to chat
          const userMessage = {
            id: Date.now().toString(),
            role: "user",
            content: response.data.user_text,
            timestamp: new Date().toISOString()
          };
          const assistantMessage = {
            id: (Date.now() + 1).toString(),
            role: "assistant",
            content: response.data.atlas_text,
            timestamp: new Date().toISOString()
          };
          setMessages(prev => [...prev, userMessage, assistantMessage]);
          
          // Play audio response
          if (response.data.audio && audioRef.current) {
            const audioBlobResponse = base64ToBlob(response.data.audio, 'audio/mp3');
            const audioUrl = URL.createObjectURL(audioBlobResponse);
            audioRef.current.src = audioUrl;
            audioRef.current.play();
          }
          
          // Continue voice mode
          if (voiceMode) {
            audioRef.current.onended = () => {
              setTimeout(() => startVoiceConversation(), 500);
            };
          }
        } catch (error) {
          console.error("Voice conversation error:", error);
          alert("Voice conversation error. Please try again.");
        } finally {
          setIsLoading(false);
        }
      };

      mediaRecorderRef.current.onerror = (event) => {
        console.error("MediaRecorder error:", event.error);
      };

      // Start recording with timeslice
      mediaRecorderRef.current.start(1000);
      setIsListening(true);
      
      // Auto-stop after 10 seconds of recording
      setTimeout(() => {
        if (mediaRecorderRef.current && isListening) {
          mediaRecorderRef.current.stop();
          setIsListening(false);
        }
      }, 10000);
    } catch (error) {
      console.error("Voice mode error:", error);
    }
  };

  const toggleVoiceMode = () => {
    if (voiceMode) {
      setVoiceMode(false);
      if (mediaRecorderRef.current && isListening) {
        mediaRecorderRef.current.stop();
        setIsListening(false);
      }
    } else {
      setVoiceMode(true);
      startVoiceConversation();
    }
  };

  // Send message to Atlas
  const sendMessage = async (text = inputText) => {
    if (!text.trim() || isLoading) return;

    const userMessage = {
      id: Date.now().toString(),
      role: "user",
      content: text,
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputText("");
    setIsLoading(true);

    try {
      const response = await axios.post(`${API}/chat`, {
        message: text,
        model: "gpt-5.2",
        session_id: "khristian-main",
        web_search_enabled: false,
        document_context: documentContext || null
      });

      const assistantMessage = {
        id: (Date.now() + 1).toString(),
        role: "assistant",
        content: response.data.response,
        timestamp: new Date().toISOString()
      };

      setMessages(prev => [...prev, assistantMessage]);

      // Reload memories if new ones were extracted
      if (response.data.memories_extracted?.length > 0) {
        loadMemories();
      }

      // Speak response if voice enabled
      if (voiceEnabled && response.data.response) {
        speakText(response.data.response);
      }

    } catch (error) {
      console.error("Chat error:", error);
      const errorMessage = {
        id: (Date.now() + 1).toString(),
        role: "assistant",
        content: "I'm afraid I encountered a spot of trouble there. Could you try again?",
        timestamp: new Date().toISOString()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  // Voice: Text to Speech
  const speakText = async (text) => {
    if (isSpeaking) {
      audioRef.current?.pause();
      setIsSpeaking(false);
      return;
    }

    try {
      setIsSpeaking(true);
      const response = await axios.post(`${API}/voice/tts`, { text: text.slice(0, 4000) });
      
      const audioBlob = base64ToBlob(response.data.audio, 'audio/mp3');
      const audioUrl = URL.createObjectURL(audioBlob);
      
      if (audioRef.current) {
        audioRef.current.src = audioUrl;
        audioRef.current.play();
        audioRef.current.onended = () => setIsSpeaking(false);
      }
    } catch (error) {
      console.error("TTS error:", error);
      setIsSpeaking(false);
    }
  };

  // Voice: Speech to Text (mic button in chat input)
  const startListening = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      
      // Check supported MIME types
      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus') 
        ? 'audio/webm;codecs=opus' 
        : MediaRecorder.isTypeSupported('audio/webm') 
          ? 'audio/webm' 
          : 'audio/mp4';
      
      mediaRecorderRef.current = new MediaRecorder(stream, { mimeType });
      audioChunksRef.current = [];

      mediaRecorderRef.current.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorderRef.current.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: mimeType });
        stream.getTracks().forEach(track => track.stop());
        
        if (audioBlob.size === 0) {
          console.error("No audio data recorded");
          alert("No audio recorded. Please try again.");
          return;
        }
        
        // Send to STT
        const formData = new FormData();
        const extension = mimeType.includes('webm') ? 'webm' : 'mp4';
        formData.append('audio', audioBlob, `recording.${extension}`);
        
        try {
          setIsLoading(true);
          const response = await axios.post(`${API}/voice/stt`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          
          if (response.data.text) {
            setInputText(response.data.text);
          }
        } catch (error) {
          console.error("STT error:", error);
          alert("Could not transcribe audio. Please try again.");
        } finally {
          setIsLoading(false);
        }
      };

      mediaRecorderRef.current.onerror = (event) => {
        console.error("MediaRecorder error:", event.error);
      };

      // Start recording with timeslice
      mediaRecorderRef.current.start(1000);
      setIsListening(true);
    } catch (error) {
      console.error("Microphone access error:", error);
      alert("Could not access microphone. Please allow microphone permissions.");
    }
  };

  const stopListening = () => {
    if (mediaRecorderRef.current && isListening) {
      mediaRecorderRef.current.stop();
      setIsListening(false);
    }
  };

  // Document upload
  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/document/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setUploadedDocument({
        name: response.data.filename,
        charCount: response.data.char_count
      });
      setDocumentContext(response.data.text);
      setShowDocumentPanel(true);
    } catch (error) {
      console.error("Upload error:", error);
    }
  };

  const clearDocument = () => {
    setUploadedDocument(null);
    setDocumentContext("");
    setShowDocumentPanel(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // Delete memory
  const deleteMemory = async (memoryId) => {
    try {
      await axios.delete(`${API}/memories/${memoryId}`);
      loadMemories();
    } catch (error) {
      console.error("Delete memory error:", error);
    }
  };

  // Clear chat
  const clearChat = async () => {
    try {
      await axios.delete(`${API}/chat/history`);
      setMessages([]);
    } catch (error) {
      console.error("Clear chat error:", error);
    }
  };

  // Utility
  const base64ToBlob = (base64, type) => {
    const byteCharacters = atob(base64);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    return new Blob([byteArray], { type });
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <div className="atlas-container" data-testid="atlas-app">
      {/* Hidden audio element */}
      <audio ref={audioRef} className="hidden" />
      <input
        ref={fileInputRef}
        type="file"
        accept=".pdf,.docx,.txt"
        onChange={handleFileUpload}
        className="hidden"
        data-testid="file-input"
      />

      {/* Header */}
      <header className="atlas-header" data-testid="atlas-header">
        <div className="header-left">
          <div className="atlas-logo" data-testid="atlas-logo">
            <div className="logo-sphere">
              <div className="sphere-core">A</div>
              <div className="arc-reactor"></div>
              <div className="sphere-ring ring-1"></div>
              <div className="sphere-ring ring-2"></div>
              <div className="sphere-ring ring-3"></div>
            </div>
            <div className="logo-pulse"></div>
          </div>
          <div className="header-title">
            <h1>Atlas</h1>
            <span className="header-subtitle">THE WEIGHT OF KNOWLEDGE, CARRIED FOR YOU.</span>
          </div>
        </div>

        <div className="header-controls">
          {/* Meeting Recording */}
          <button
            className={`control-button ${isRecordingMeeting ? 'recording' : ''}`}
            onClick={isRecordingMeeting ? stopMeetingRecording : startMeetingRecording}
            title={isRecordingMeeting ? "Stop Recording" : "Record Meeting"}
            data-testid="meeting-record-button"
          >
            <Radio strokeWidth={1.5} />
          </button>

          {/* Meetings Panel */}
          <button
            className={`control-button ${showMeetingsPanel ? 'active' : ''}`}
            onClick={() => setShowMeetingsPanel(!showMeetingsPanel)}
            title="Meetings"
            data-testid="meetings-toggle"
          >
            <Calendar strokeWidth={1.5} />
            {meetings.length > 0 && <span className="badge">{meetings.length}</span>}
          </button>

          {/* Voice Conversation Mode */}
          <button
            className={`control-button ${voiceMode ? 'active voice-mode' : ''}`}
            onClick={toggleVoiceMode}
            title={voiceMode ? "Exit Voice Mode" : "Voice Conversation"}
            data-testid="voice-mode-toggle"
          >
            {voiceMode ? <MicOff strokeWidth={1.5} /> : <Mic strokeWidth={1.5} />}
          </button>

          {/* Voice Toggle */}
          <button
            className={`control-button ${voiceEnabled ? 'active' : ''}`}
            onClick={() => setVoiceEnabled(!voiceEnabled)}
            title="Voice Responses"
            data-testid="voice-toggle"
          >
            {voiceEnabled ? <Volume2 strokeWidth={1.5} /> : <VolumeX strokeWidth={1.5} />}
          </button>

          {/* Memory Panel Toggle */}
          <button
            className={`control-button ${showMemoryPanel ? 'active' : ''}`}
            onClick={() => { setShowMemoryPanel(!showMemoryPanel); setShowMeetingsPanel(false); }}
            title="Memories"
            data-testid="memory-toggle"
          >
            <Brain strokeWidth={1.5} />
            {memories.length > 0 && <span className="badge">{memories.length}</span>}
          </button>

          {/* Clear Chat - Chi Rho */}
          <button
            className="control-button chi-rho-button"
            onClick={clearChat}
            title="Clear Chat"
            data-testid="clear-chat-button"
          >
            <span className="chi-rho-icon">☧</span>
          </button>
        </div>
      </header>

      {/* Main Content */}
      <div className="atlas-main">
        {/* Chat Area */}
        <div className="chat-container" data-testid="chat-container">
          {/* Document Banner */}
          {uploadedDocument && (
            <div className="document-banner" data-testid="document-banner">
              <FileText strokeWidth={1.5} />
              <span>{uploadedDocument.name} ({uploadedDocument.charCount.toLocaleString()} chars)</span>
              <button onClick={clearDocument} data-testid="clear-document-button">
                <X strokeWidth={1.5} />
              </button>
            </div>
          )}

          {/* Messages */}
          <div className="messages-area" data-testid="messages-area">
            {messages.length === 0 ? (
              <div className="empty-state" data-testid="empty-state">
                <div className="chi-rho-container">
                  <div className="chi-rho-symbol">☧</div>
                  <div className="chi-rho-orbit orbit-1"></div>
                  <div className="chi-rho-orbit orbit-2"></div>
                  <div className="chi-rho-orbit orbit-3"></div>
                  <div className="chi-rho-glow"></div>
                </div>
                <h2>"I carry the weight. You lead the mission."</h2>
                <p>How may I assist you today, Khristian?</p>
              </div>
            ) : (
              messages.map((message) => (
                <div 
                  key={message.id} 
                  className={`message ${message.role}`}
                  data-testid={`message-${message.role}`}
                >
                  {message.role === 'assistant' && (
                    <div className="message-avatar">
                      <Sparkles strokeWidth={1.5} />
                    </div>
                  )}
                  <div className="message-content">
                    <p>{message.content}</p>
                    {message.role === 'assistant' && (
                      <button 
                        className="speak-button"
                        onClick={() => speakText(message.content)}
                        data-testid="speak-message-button"
                      >
                        <Volume2 strokeWidth={1.5} />
                      </button>
                    )}
                  </div>
                </div>
              ))
            )}
            
            {isLoading && (
              <div className="message assistant loading" data-testid="loading-indicator">
                <div className="message-avatar">
                  <Sparkles strokeWidth={1.5} />
                </div>
                <div className="message-content">
                  <div className="typing-indicator">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                </div>
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>

          {/* Input Area */}
          <div className="input-area" data-testid="input-area">
            <div className="input-container">
              <button
                className="input-action"
                onClick={() => fileInputRef.current?.click()}
                title="Upload Document"
                data-testid="upload-button"
              >
                <Upload strokeWidth={1.5} />
              </button>

              <textarea
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Ask Atlas anything..."
                rows={1}
                data-testid="chat-input"
              />

              <button
                className={`input-action mic ${isListening ? 'listening' : ''}`}
                onClick={isListening ? stopListening : startListening}
                title={isListening ? "Stop Recording" : "Voice Input"}
                data-testid="mic-button"
              >
                {isListening ? <MicOff strokeWidth={1.5} /> : <Mic strokeWidth={1.5} />}
              </button>

              <button
                className="send-button"
                onClick={() => sendMessage()}
                disabled={!inputText.trim() || isLoading}
                data-testid="send-button"
              >
                <Send strokeWidth={1.5} />
              </button>
            </div>

            {uploadedDocument && (
              <div className="input-footer">
                <span className="feature-tag"><FileText strokeWidth={1.25} /> {uploadedDocument.name}</span>
              </div>
            )}
          </div>
        </div>

        {/* Memory Panel */}
        {showMemoryPanel && (
          <div className="memory-panel" data-testid="memory-panel">
            <div className="panel-header">
              <h3><Brain strokeWidth={1.5} /> Memories</h3>
              <button onClick={() => setShowMemoryPanel(false)} data-testid="close-memory-panel">
                <X strokeWidth={1.5} />
              </button>
            </div>
            
            <div className="panel-content">
              {memories.length === 0 ? (
                <p className="empty-memories">No memories yet. As we chat, I'll learn about you.</p>
              ) : (
                memories.map(memory => (
                  <div key={memory.id} className="memory-item" data-testid={`memory-${memory.id}`}>
                    <p>{memory.content}</p>
                    <div className="memory-meta">
                      <span className="memory-category">{memory.category}</span>
                      <button 
                        onClick={() => deleteMemory(memory.id)}
                        data-testid={`delete-memory-${memory.id}`}
                      >
                        <Trash2 strokeWidth={1.5} />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* Meetings Panel */}
        {showMeetingsPanel && (
          <div className="meetings-panel" data-testid="meetings-panel">
            <div className="panel-header">
              <h3><Calendar strokeWidth={1.5} /> Meetings</h3>
              <button onClick={() => setShowMeetingsPanel(false)} data-testid="close-meetings-panel">
                <X strokeWidth={1.5} />
              </button>
            </div>
            
            {/* Search */}
            <div className="meetings-search">
              <Search strokeWidth={1.5} />
              <input
                type="text"
                placeholder="Search meetings..."
                value={meetingSearch}
                onChange={(e) => {
                  setMeetingSearch(e.target.value);
                  loadMeetings(e.target.value);
                }}
                data-testid="meetings-search"
              />
            </div>
            
            <div className="panel-content">
              {meetings.length === 0 ? (
                <p className="empty-meetings">No meetings yet. Record your first meeting!</p>
              ) : (
                meetings.map(meeting => (
                  <div 
                    key={meeting.id} 
                    className={`meeting-item ${selectedMeeting?.id === meeting.id ? 'selected' : ''}`}
                    onClick={() => setSelectedMeeting(selectedMeeting?.id === meeting.id ? null : meeting)}
                    data-testid={`meeting-${meeting.id}`}
                  >
                    <div className="meeting-header">
                      <h4>{meeting.title}</h4>
                      <span className={`priority-badge ${meeting.priority}`}>{meeting.priority}</span>
                    </div>
                    <div className="meeting-meta">
                      <span><Clock strokeWidth={1.25} /> {new Date(meeting.date).toLocaleDateString()}</span>
                      <span className="topic-tag">{meeting.topic}</span>
                    </div>
                    
                    {selectedMeeting?.id === meeting.id && (
                      <div className="meeting-details">
                        {meeting.summary && (
                          <div className="meeting-section">
                            <h5>Summary</h5>
                            <p>{meeting.summary}</p>
                          </div>
                        )}
                        {meeting.key_points?.length > 0 && (
                          <div className="meeting-section">
                            <h5>Key Points</h5>
                            <ul>
                              {meeting.key_points.map((point, i) => (
                                <li key={i}>{point}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {meeting.action_items?.length > 0 && (
                          <div className="meeting-section action-items">
                            <h5>Action Items</h5>
                            <ul>
                              {meeting.action_items.map((item, i) => (
                                <li key={i}>{item}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {meeting.decisions?.length > 0 && (
                          <div className="meeting-section">
                            <h5>Decisions</h5>
                            <ul>
                              {meeting.decisions.map((decision, i) => (
                                <li key={i}>{decision}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {meeting.follow_ups?.length > 0 && (
                          <div className="meeting-section follow-ups">
                            <h5>Follow-ups</h5>
                            <ul>
                              {meeting.follow_ups.map((followUp, i) => (
                                <li key={i}>{followUp}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    )}
                    
                    <ChevronRight className={`expand-icon ${selectedMeeting?.id === meeting.id ? 'expanded' : ''}`} strokeWidth={1.5} />
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default App;
